import { Stack, Typography, useTheme } from "@mui/material";
import { ChevronRight } from "lucide-react";
import type { FC } from "react";
import { useNavigate } from "react-router-dom";
import type { TaskListItemResponseDTO } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { AppRouting } from "web_myplann/Infrastructure/Utils/UrlUtils";
import { TasksIcoIcon } from "web_myplann/Shared/Icons";
import { dateFormatter } from "web_myplann/Shared/Utils/formatters";
import { CIRCLE_SIGN, LIST_ICON_SIZE } from "web_myplann/Utils/constants";

type Props = { data: TaskListItemResponseDTO; hasMoreConnectedAgents: boolean };

export const TaskItem: FC<Props> = ({
	data: { id, title, date, author, time },
	hasMoreConnectedAgents,
}) => {
	const { t } = useResource();
	const theme = useTheme();
	const navigate = useNavigate();

	const taskLabel = t(Resources.Activity.List.Task);
	const enhancedTitle = title
		? `${taskLabel} ${CIRCLE_SIGN} ${title}`
		: taskLabel;

	const navigateToEvent = (id: string) => {
		navigate(AppRouting.MyPlann.TaskDetail.path(id));
	};

	return (
		<Stack
			direction={"row"}
			justifyContent={"space-between"}
			alignItems={"center"}
			onClick={() => navigateToEvent(String(id))}
			sx={{ cursor: "pointer" }}
		>
			<Stack
				direction={"row"}
				gap={1}
				alignItems={"center"}
				flex={1}
				minWidth={0}
			>
				<Stack
					width={32}
					height={32}
					alignItems="center"
					justifyContent="center"
				>
					<TasksIcoIcon width={LIST_ICON_SIZE} height={LIST_ICON_SIZE} />
				</Stack>
				<Stack direction={"column"} flex={1} minWidth={0}>
					{date && (
						<Typography
							fontSize={12}
							fontWeight={400}
							color={theme.palette.grey[500]}
						>
							{dateFormatter(date ?? new Date())} {time?.slice(0, -3)}
						</Typography>
					)}

					<Typography
						fontSize={14}
						fontWeight={600}
						color={theme.palette.grey[700]}
						sx={{
							whiteSpace: "pre-line",
							wordBreak: "normal",
							overflowWrap: "break-word",
							display: "-webkit-box",
							WebkitBoxOrient: "vertical",
							WebkitLineClamp: 2,
							overflow: "hidden",
						}}
					>
						{enhancedTitle}
					</Typography>

					{hasMoreConnectedAgents && (
						<Typography
							fontSize={12}
							fontWeight={400}
							color={theme.palette.grey[500]}
							sx={{
								whiteSpace: "pre-line",
								wordBreak: "break-all",
								display: "-webkit-box",
								WebkitBoxOrient: "vertical",
								WebkitLineClamp: 1,
								overflow: "hidden",
							}}
						>
							{author.name} ({t(Resources.Dashboard.UserType[author.type])})
						</Typography>
					)}
				</Stack>
			</Stack>

			<Stack pl={1.2}>
				<ChevronRight
					size={16}
					strokeWidth={1.5}
					cursor={"pointer"}
					color={theme.palette.primary.main}
				/>
			</Stack>
		</Stack>
	);
};
